import { createFileRoute, useNavigate, Link, redirect } from '@tanstack/react-router'
import { assetPath } from '@/utils/assetPath'
import { useState, useEffect, useCallback } from 'react'
import { ArrowLeft, AlertCircle } from 'lucide-react'
import { GlossyButton } from '@/components/shared/atoms/GlossyButton'
import { GlossyInput } from '@/components/auth/GlossyInput'
import { FormErrorMessage } from '@/components/auth/FormErrorMessage'
import { FadeInUp } from '@/components/shared/atoms/FadeInUp'
import { useOnboardingStore, waitForOnboardingHydration } from '@/stores/onboarding'
import {
    useRegisterStylist,
    useGoogleCompleteRegistration,
    useAppleCompleteRegistration,
    useLogin,
    useSendEmailVerification,
} from '@/hooks/api/useAuth'
import { Role } from '@wtf/shared'
import { usePostHog } from 'posthog-js/react'
import { AnalyticsEvents } from '@/utils/analytics'
import { getIdToken } from '@/auth/firebase'
import { captureException } from '@/config/sentry'

export const Route = createFileRoute('/(onboarding)/(auth)/register/instagram')(
    {
        validateSearch: (search) => ({
            returnToPath:
                typeof search.returnToPath === 'string'
                    ? search.returnToPath
                    : undefined,
            error:
                typeof search.error === 'string'
                    ? search.error
                    : undefined,
        }),
        beforeLoad: async () => {
            // Wait for store hydration from sessionStorage before checking state
            await waitForOnboardingHydration()
            const { registrationData } = useOnboardingStore.getState()
            if (!registrationData.accountType) {
                throw redirect({ to: '/register/account-type', search: { returnToPath: undefined, error: 'missing-account-type' } })
            }
        },
        component: RegisterInstagramRoute,
    }
)

function RegisterInstagramRoute() {
    const {
        registrationData,
        setRegistrationData,
        markStepCompleted,
        resetRegistrationData,
    } = useOnboardingStore()
    const navigate = useNavigate()
    const { returnToPath } = Route.useSearch()
    const [instagramUsername, setInstagramUsername] = useState(
        registrationData.instagramUsername || ''
    )
    const [apiError, setApiError] = useState<string | null>(null)
    const [validationError, setValidationError] = useState<string | null>(null)
    const posthog = usePostHog()

    // API hooks
    const { mutate: registerStylist, isPending: isRegisteringStylist } =
        useRegisterStylist()
    const {
        mutate: googleCompleteRegistration,
        isPending: isRegisteringWithGoogle,
    } = useGoogleCompleteRegistration()
    const {
        mutate: appleCompleteRegistration,
        isPending: isRegisteringWithApple,
    } = useAppleCompleteRegistration()
    const { mutate: loginUser } = useLogin()
    const { mutate: sendVerification } = useSendEmailVerification()

    const isLoading = isRegisteringStylist || isRegisteringWithGoogle || isRegisteringWithApple
    const isSocialSignIn = registrationData.authMethod === 'google' || registrationData.authMethod === 'apple'

    // Instagram username validation
    useEffect(() => {
        if (instagramUsername) {
            if (instagramUsername.length > 30) {
                setValidationError(
                    'Instagram username must be at most 30 characters'
                )
            } else if (!/^[a-zA-Z0-9_.]+$/.test(instagramUsername)) {
                setValidationError(
                    'Only letters, numbers, periods, and underscores allowed'
                )
            } else if (/\.\./.test(instagramUsername)) {
                setValidationError('Cannot contain consecutive periods')
            } else if (instagramUsername.endsWith('.')) {
                setValidationError('Cannot end with a period')
            } else {
                setValidationError(null)
            }
        } else {
            setValidationError('Instagram username is required')
        }
    }, [instagramUsername])

    // Comprehensive error message handler matching account-type.tsx patterns
    const getErrorMessage = (error: Error | null): string | null => {
        if (!error) return null
        const msg = error.message?.toLowerCase() || ''

        // Username conflict
        if (msg.includes('username already exists')) {
            return 'Username conflict during final registration. Please go back.'
        }

        // Instagram username conflict
        if (msg.includes('instagram username already exists')) {
            return 'This Instagram username is already registered. Please use a different one.'
        }

        // Comprehensive email conflict detection
        if (
            (msg.includes('email') &&
                (msg.includes('exist') ||
                    msg.includes('already') ||
                    msg.includes('registered') ||
                    msg.includes('taken'))) ||
            (msg.includes('user') &&
                msg.includes('email') &&
                (msg.includes('exist') || msg.includes('already'))) ||
            msg.includes('email already') ||
            msg.includes('already registered') ||
            msg.includes('account with this email')
        ) {
            return 'An account with this email address already exists. Please log in or use a different email.'
        }

        // Token expiration
        if (msg.includes('invalid token') || msg.includes('expired')) {
            return 'Authentication session expired. Please sign in again.'
        }

        return 'Registration failed. Please check your details and try again.'
    }

    const handleBack = useCallback(() => {
        // Go back to account type selection
        navigate({ to: '/register/account-type', search: { returnToPath, error: undefined } })
    }, [navigate, returnToPath])

    const handleRegistrationSuccess = useCallback(() => {
        posthog?.capture(AnalyticsEvents.ONBOARDING_COMPLETED, {
            account_type: registrationData.accountType,
            public_profile: !!registrationData.instagramUsername,
            registration_method: registrationData.authMethod || 'email',
        })

        resetRegistrationData()

        // Check for marker to handle cleanup
        const markerStr = sessionStorage.getItem('authFlowMarker')
        if (markerStr) {
            try {
                const marker = JSON.parse(markerStr) as {
                    historyLength: number
                    startPath: string
                }
                const stepsBack = window.history.length - marker.historyLength
                if (stepsBack > 0) {
                    window.history.go(-stepsBack)
                    return
                }
            } catch (e) {
                console.error('Marker cleanup failed:', e)
                sessionStorage.removeItem('authFlowMarker')
            }
        }
        navigate({ to: returnToPath || '/' })
    }, [
        posthog,
        registrationData.accountType,
        registrationData.instagramUsername,
        registrationData.authMethod,
        resetRegistrationData,
        navigate,
        returnToPath,
    ])

    const handleContinue = useCallback(async () => {
        if (!instagramUsername || validationError || isLoading) return

        setApiError(null)
        setRegistrationData({ instagramUsername }) // Store value
        markStepCompleted('instagram-username')

        const role = Role.STYLIST // Always STYLIST for this page

        if (isSocialSignIn) {
            if (!registrationData.username) {
                setApiError(
                    'Your username is missing. Please go back and enter a username.'
                )
                return
            }
            if (!registrationData.registrationToken) {
                setApiError(
                    'Your authentication session is missing. Please restart the registration.'
                )
                return
            }

            // Refresh token before completing registration to prevent expiration errors
            const freshToken = await getIdToken(true)
            if (!freshToken) {
                setApiError(
                    'Authentication session expired. Please sign in again.'
                )
                return
            }

            const registrationPayload = {
                idToken: freshToken,
                role: role,
                username: registrationData.username,
                instagramUsername: instagramUsername,
                stylePreference: registrationData.preference!,
            }

            const onSuccess = handleRegistrationSuccess
            const onError = (err: Error) => {
                captureException(err, {
                    flow: 'registration',
                    step: 'instagram',
                    authMethod: registrationData.authMethod,
                })
                setApiError(getErrorMessage(err))
            }

            if (registrationData.authMethod === 'google') {
                googleCompleteRegistration(registrationPayload, { onSuccess, onError })
            } else if (registrationData.authMethod === 'apple') {
                appleCompleteRegistration(registrationPayload, { onSuccess, onError })
            }
        } else {
            // Standard Email/Password Registration
            const missingFields: string[] = []
            if (!registrationData.email) missingFields.push('email')
            if (!registrationData.password) missingFields.push('password')
            if (!registrationData.fullName) missingFields.push('full name')
            if (!registrationData.username) missingFields.push('username')

            if (missingFields.length > 0) {
                setApiError(
                    `Your ${missingFields.join(', ')} ${missingFields.length === 1 ? 'is' : 'are'} missing. Please restart the registration.`
                )
                return
            }
            const userData = {
                email: registrationData.email!,
                password: registrationData.password!,
                username: registrationData.username!,
                name: registrationData.fullName!,
                instagramUsername: instagramUsername,
                stylePreference: registrationData.preference!,
            }
            registerStylist(userData, {
                onSuccess: () => {
                    // Auto login after successful registration, then send verification
                    loginUser(
                        {
                            email: registrationData.email!,
                            password: registrationData.password!,
                        },
                        {
                            onSuccess: () => {
                                sendVerification(
                                    {
                                        continueUrl: returnToPath || '/',
                                    },
                                    {
                                        onSuccess: () => {
                                            // Verification email sent successfully
                                            handleRegistrationSuccess()
                                        },
                                        onError: () => {
                                            // Even if email verification fails, continue with registration success
                                            handleRegistrationSuccess()
                                        },
                                    }
                                )
                            },
                            onError: () =>
                                setApiError(
                                    'Registration succeeded but auto-login failed. Please log in manually.'
                                ),
                        }
                    )
                },
                onError: (err) => {
                    captureException(err, {
                        flow: 'registration',
                        step: 'instagram',
                        authMethod: 'email',
                    })
                    setApiError(getErrorMessage(err))
                },
            })
        }
    }, [
        instagramUsername,
        validationError,
        isLoading,
        registrationData,
        isSocialSignIn,
        setRegistrationData,
        markStepCompleted,
        googleCompleteRegistration,
        appleCompleteRegistration,
        registerStylist,
        handleRegistrationSuccess,
        loginUser,
        sendVerification,
        returnToPath,
    ])

    const isContinueDisabled =
        isLoading || !instagramUsername || !!validationError

    return (
        <section className="relative z-10 flex min-h-dvh flex-col pt-safe">
            <div className="flex px-2 pt-3 md:justify-center">
                <img
                    alt="logo-red"
                    src={assetPath('logo/logo-red.svg')}
                    className="h-[34px] w-[155px] md:mt-12 md:h-[100px] md:w-[440px]"
                />
            </div>

            <FadeInUp delay={0.3}>
                <div className="px-4 pt-2 pb-6 md:pt-[35vh] md:text-center">
                    <h1 className="font-sfpro text-28 leading-8 font-semibold text-white">
                        Enter your
                        <br className="md:hidden" />
                        <span className="text-white-48">
                            {' '}
                            Instagram username
                        </span>
                    </h1>
                    <p className="mt-3 text-sm text-white">
                        This is the account that will be linked to your profile.
                        We will review your account within 48 hours and direct
                        message you for confirmation. You can start setting up
                        your creator account while we review your profile.
                    </p>
                </div>
            </FadeInUp>

            <div className="flex flex-1 flex-col px-2">
                <FadeInUp delay={0.5}>
                    <div>
                        <GlossyInput
                            placeholder="Instagram Username"
                            value={instagramUsername}
                            onChange={(e) =>
                                setInstagramUsername(e.target.value)
                            }
                            glassEffect={{
                                opacity: 'bg-white/20',
                                blur: 'backdrop-blur-md',
                            }}
                            focusedBorderColor={
                                validationError
                                    ? 'border-red-500'
                                    : 'border-white'
                            }
                            unfocusedBorderColor={
                                validationError
                                    ? 'border-red-500/70'
                                    : 'border-white/30'
                            }
                            disabled={isLoading}
                        />
                        {validationError && (
                            <FormErrorMessage
                                message={validationError}
                                center
                                className="mt-1 text-xs"
                            />
                        )}
                        {apiError && (
                            <div className="mt-3 flex items-center justify-center gap-2 rounded-lg bg-red-500/20 p-2 backdrop-blur">
                                <AlertCircle
                                    className="shrink-0 text-red-500"
                                    size={16}
                                />
                                <p className="text-center text-xs text-red-400">
                                    {apiError}
                                </p>
                            </div>
                        )}
                    </div>
                </FadeInUp>

                <FadeInUp delay={0.7} className="mt-auto mb-6">
                    <div className="flex items-center gap-2 pt-6">
                        <GlossyButton
                            variant="ghost"
                            className="rounded-xl bg-white/20 px-4 py-[14px] text-sm font-medium text-white backdrop-blur-md"
                            onClick={handleBack}
                            disabled={isLoading}
                        >
                            <ArrowLeft
                                strokeWidth={1}
                                className="mr-1 h-5 w-5"
                            />
                            Back
                        </GlossyButton>
                        <GlossyButton
                            className="w-full rounded-xl py-[14px] text-sm"
                            onClick={handleContinue}
                            disabled={isContinueDisabled}
                        >
                            {isLoading
                                ? 'Creating Account...'
                                : 'Create Account'}
                        </GlossyButton>
                    </div>

                    <div className="mt-4 px-4 text-center">
                        <p className="text-sm font-normal text-white">
                            By creating an account, you agree that you have read
                            and accepted our{' '}
                            <Link
                                to="/terms-of-use"
                                className="font-semibold underline hover:text-white/80"
                            >
                                Terms of use.
                            </Link>
                        </p>
                    </div>
                </FadeInUp>
            </div>
        </section>
    )
}
